import { getCompleteSizes } from '@cian/adfox-component';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';
import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useSelector } from 'react-redux';
import { AdfoxBanner } from 'shared/components/AdfoxBanner';
import { OFFER_CARD_DESKTOP_CARD_ADFOX_PLACEMENTS } from 'shared/constants/adfoxPlacements';
import { AdBannerMicrofrontend } from 'shared/containers/AdBannerMicrofrontend';
import { ApplicationContext, useApplicationContext } from 'shared/containers/ApplicationContext';
import { TAdfoxContextData, AdfoxContext } from 'shared/context/AdfoxContext';
import { useAdfoxParams } from 'shared/context/AdfoxContext/internal/hooks/useAdfoxPuids';
import { selectIsBookedFromDeveloper } from 'shared/selectors/newbuildingBookedFromDeveloper';
import { selectIsNewbuildingPremiumRate } from 'shared/selectors/newbuildingPremiumRate';
import { regionIdSelector } from 'shared/selectors/offerData/geo/regionIdSelector';
import { useAdBannerMicrofrontendEnabled } from 'shared/utils/adfox';

const PLACEMENT = OFFER_CARD_DESKTOP_CARD_ADFOX_PLACEMENTS.above_header;

export const StretchBannerContainer: React.FC = () => {
  const stretchBannerRoot = React.useRef<Root | null>(null);
  const elementRef = React.useRef<HTMLElement | null>(null);

  const context = useApplicationContext();
  const isMicrofrontendEnabled = useAdBannerMicrofrontendEnabled();

  const params = useAdfoxParams(PLACEMENT.pParams);

  const isPremiumNewbuilding = useSelector(selectIsNewbuildingPremiumRate);
  const isBookedFromDeveloper = useSelector(selectIsBookedFromDeveloper);
  const regionId = useSelector(regionIdSelector);

  const adfoxContextProviderValue = React.useMemo(
    (): TAdfoxContextData => ({
      postViewAnalyticsData: PLACEMENT.postViewAnalyticsData,
      sizes: getCompleteSizes(PLACEMENT.sizes),
      params,
      isHidden: isPremiumNewbuilding || isBookedFromDeveloper,
      regionId,
    }),
    [isPremiumNewbuilding, isBookedFromDeveloper, params, regionId],
  );

  React.useEffect(() => {
    if (isMicrofrontendEnabled && adfoxContextProviderValue.isHidden) {
      return;
    }

    /* istanbul ignore next */
    if (!stretchBannerRoot.current) {
      const el = document.getElementById('adfox-stretch-banner');

      /* istanbul ignore next */
      if (el) {
        elementRef.current = el;
        stretchBannerRoot.current = createRoot(el);

        stretchBannerRoot.current.render(
          <ApplicationContext.Provider value={context}>
            <AdfoxContext.Provider value={adfoxContextProviderValue}>
              {isMicrofrontendEnabled ? (
                <MicrofrontendProvider registry={context.microfrontendsRegistry}>
                  <AdBannerMicrofrontend />
                </MicrofrontendProvider>
              ) : (
                <AdfoxBanner />
              )}
            </AdfoxContext.Provider>
          </ApplicationContext.Provider>,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
